import {PropsWithChildren, useEffect, useMemo} from "react";
import {useScopedCollection} from "./useScopedCollection";
import {collection, DocumentData, query, QueryConstraint, where} from 'firebase/firestore';
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {firebaseDateToDate} from "../app/tools";
import { setBankHolidays,} from "../features/AppDataSlice";
import {RRBankHolidaysType, RRBankHolidayType} from "../app/types/RRBankHolidayType";
import {selectCalendarFirstDate, selectCalendarVisibleDays, selectDepartment,} from "../features/AppStateSlice";
import {RRDEPARTMENT_MONTAGE, stringAsDepartment} from "../app/types/RRDepartmentType";

export const bankHolidayDataConverter = (value: DocumentData): RRBankHolidayType => {
    return {
        id: value.id,
        ref: value.ref,
        date: value.get("date"),
        dateObject: firebaseDateToDate(value.get("dateObject")),
        department: value.get("department")
    } as RRBankHolidayType;
}

export const bankHolidaysDataConverter = (values: DocumentData[]): RRBankHolidaysType => {
    if(!values) return [];

    return values.map(
        value => bankHolidayDataConverter(value)
    )
}

export interface Props{
}
export const FirebaseBankHolidaysLoader: React.FC<PropsWithChildren<Props>> = (props) => {
    const dispatch = useAppDispatch();
    const calendarFirstDate = useAppSelector(selectCalendarFirstDate);
    const calendarDays = useAppSelector(selectCalendarVisibleDays);
    const activeDepartment = stringAsDepartment(useAppSelector(selectDepartment));

    const firstDay = useMemo(() => {
        const date = new Date(calendarFirstDate);
        date.setHours(0, 0, 0, 0);
        return date;
    }, [calendarFirstDate]);
    const lastDay = useMemo(() => {
        const date = new Date(firstDay);
        date.setDate(firstDay.getDate() + calendarDays);
        return date;
    }, [firstDay, calendarDays]);

    const constraints = useMemo<QueryConstraint[]>(() => [
        where("dateObject", ">=", firstDay),
        where("dateObject", "<=", lastDay),
        where("department", "==", activeDepartment || RRDEPARTMENT_MONTAGE)
    ], [activeDepartment, firstDay, lastDay]);
    const bankHolidaysQuery = useMemo(
        () => query(collection(appFirestore, 'bankHolidays'), ...constraints),
        [constraints]
    );
    const {data, loading, error, subscription} = useScopedCollection(
        bankHolidaysQuery, `${activeDepartment}:${firstDay.getTime()}:${lastDay.getTime()}`
    );

    useEffect(() => {
        if (data) {
            dispatch(setBankHolidays(
                {
                    loading: loading, error: error?.message, data: data ? bankHolidaysDataConverter(data.docs) : []
                }));
        }
    }, [data, loading, error]);

    return <>{subscription}{props.children}</>;
}
